import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  NewspaperIcon,
  ChartBarIcon,
  GlobeAltIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const News = () => {
  const { t, i18n } = useTranslation();
  
  console.log('Current language:', i18n.language);
  
  // 添加默认值和错误处理
  let news = [];
  let insights = [];
  
  try {
    const newsFromTranslation = t('news.news', { returnObjects: true });
    const insightsFromTranslation = t('news.insights', { returnObjects: true });
    
    news = Array.isArray(newsFromTranslation) ? newsFromTranslation : 
           (typeof newsFromTranslation === 'object' ? [newsFromTranslation] : []);
    
    insights = Array.isArray(insightsFromTranslation) ? insightsFromTranslation : 
               (typeof insightsFromTranslation === 'object' ? [insightsFromTranslation] : []);
  } catch (error) {
    console.error('Error loading news data:', error);
  }
  
  console.log('News array:', news);
  console.log('Insights array:', insights);
  console.log('Is news an array?', Array.isArray(news));
  console.log('Is insights an array?', Array.isArray(insights));

  return (
    <>
      <Helmet>
        <title>{t('news.meta.title')}</title>
        <meta name="description" content={t('news.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl font-bold mb-6">{t('news.hero.title')}</h1>
            <p className="text-xl text-gray-200">
              {t('news.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured News */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('news.sections.latestNews')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.map((item, index) => (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div 
                  className={`w-full h-48 flex items-center justify-center text-white ${
                    index === 0 ? 'bg-[#3B82F6]' : index === 1 ? 'bg-[#1E40AF]' : 'bg-[#1E293B]'
                  }`}
                >
                  <div className="text-center">
                    {index === 0 ? (
                      <NewspaperIcon className="h-16 w-16 mx-auto text-white mb-2" />
                    ) : index === 1 ? (
                      <SparklesIcon className="h-16 w-16 mx-auto text-white mb-2" />
                    ) : (
                      <ChartBarIcon className="h-16 w-16 mx-auto text-white mb-2" />
                    )}
                    <div className="text-lg font-medium">{item.category}</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{item.date}</span>
                    <button className="text-primary font-medium hover:text-primary/80">
                      {t('news.sections.readMore')}
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Market Insights */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('news.sections.marketInsights')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {insights.map((insight, index) => (
              <motion.div
                key={insight.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {index === 0 ? (
                    <ChartBarIcon className="h-6 w-6 text-primary mr-3" />
                  ) : index === 1 ? (
                    <GlobeAltIcon className="h-6 w-6 text-primary mr-3" />
                  ) : (
                    <SparklesIcon className="h-6 w-6 text-primary mr-3" />
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2">{insight.title}</h3>
                <p className="text-gray-600 mb-4">{insight.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">{insight.date}</span>
                  <button className="text-primary font-medium hover:text-primary/80">
                    {t('news.sections.viewDetails')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">{t('news.sections.newsletter.title')}</h2>
            <p className="text-gray-600 mb-8">
              {t('news.sections.newsletter.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <input
                type="email"
                placeholder={t('news.sections.newsletter.emailPlaceholder')}
                className="w-full sm:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => alert(t('news.sections.newsletter.notAvailable'))}
                className="w-full sm:w-auto px-6 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed hover:bg-gray-500 transition-colors duration-300"
              >
                {t('news.sections.newsletter.subscribeButton')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default News; 